import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { tracker } from '$utils/tracker';
import { UserTypes } from '$types/UserTypes';
import { UserCandidate } from '../../../../types/serverTypes/userDto';
import { ICreateUser } from '../../../legacy/auth/auth.actions';
import { emailSignUp } from '../../../../api/auth.service';
import {
  CandidateBuilder,
  EmailInternalAuthInput,
  EmailInternalAuthOutput,
} from '../candidates.types';
import {
  SuccessMessageEs,
  SuccessMessageEn,
  ErrorMessageEn,
  ErrorMessageEs,
} from '../../../../constants/alerts';
import { errorHandler, successHandler } from '../../../utils';

/** Auth0's codes for a sign-up whose email it already holds (which one depends on the tenant). */
const AUTH0_EMAIL_TAKEN = ['user_exists', 'invalid_signup'];

/** POST /candidates/internal/email-lookup: the account behind an email, as far as this staff member may see. */
export type EmailLookupResult =
  | { found: false }
  | { found: true; visible: false }
  | {
      found: true;
      visible: true;
      tenantName: string;
      candidateId: number | null;
    };

type ExistingAccount = Exclude<EmailLookupResult, { found: false }>;

/** What staff should do instead of creating: open the profile, ask support to move it, or stop. */
const existingAccountMessage = (
  account: ExistingAccount,
  spanish: boolean
): string => {
  if (!account.visible) {
    return spanish
      ? 'Este correo ya tiene un perfil en otro tenant. Pide a soporte que lo mueva; no lo crees con otro correo.'
      : "This email already has a profile in another tenant. Ask support to move it; don't create it with another email.";
  }
  if (account.candidateId === null) {
    return spanish
      ? `Este correo pertenece a una cuenta de empresa o de staff en ${account.tenantName}; no puede ser también candidato.`
      : `This email belongs to a company or staff account in ${account.tenantName}; it can't also be a candidate.`;
  }
  return spanish
    ? `Este correo ya tiene un perfil en ${account.tenantName}. Ábrelo y edítalo; no lo crees de nuevo.`
    : `This email already has a profile in ${account.tenantName}. Open it and edit it; don't create it again.`;
};

class CustomFetchQueryError extends Error {
  constructor(
    public status: number,
    public data: unknown,
    public error: string
  ) {
    super();
  }
}

const mapUserData = (
  user: ICreateUser,
  auth0Id: string,
  referrerId: string | null
) => ({
  FirstName: user.firstName,
  LastName: user.lastName,
  Email: user.email,
  Phone: user.phone,
  ResidenceCountry: user.residenceCountry,
  SignUpType: 0,
  ...(referrerId && { referrerId }),
  Auth0Id: auth0Id,
  Score: 0,
  IsInternallyCreated: true,
  TenantId: user.tenantId,
});

export const emailInternalAuth = (builder: CandidateBuilder) =>
  builder.mutation<EmailInternalAuthOutput, EmailInternalAuthInput>({
    async queryFn(arg, _api, _extraOptions, baseQuery) {
      const referralId = localStorage.getItem('referralId');
      const spanish = (arg.lang ?? 'en') === 'es';
      // Asked before Auth0, whose refusal can't tell "profile exists" from "login only". If the check
      // fails, the sign-up still runs and Auth0 still refuses a taken email.
      const lookup = await baseQuery({
        url: '/candidates/internal/email-lookup',
        method: 'POST',
        body: { Email: arg.user.email },
      });
      const account = lookup.data as EmailLookupResult | undefined;
      if (account?.found) {
        const message = existingAccountMessage(account, spanish);
        errorHandler('[EmailInternalAuthAction]', message, account, true);
        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: message,
            data: account,
          } as FetchBaseQueryError,
        };
      }
      let emailResponse;
      try {
        emailResponse = await emailSignUp(
          arg.user.email,
          arg.user.password,
          'candidate'
        );
      } catch (error: any) {
        // auth0-js rejects with { code, description }. An email Auth0 already holds means the login
        // exists without a profile here; retyping the email to get past it makes a wrong profile.
        const exists = AUTH0_EMAIL_TAKEN.includes(error?.code);
        const notCreated = spanish
          ? ErrorMessageEs.USER_NOT_CREATED
          : ErrorMessageEn.USER_NOT_CREATED;
        let message: string = notCreated;
        if (exists) {
          message = spanish
            ? ErrorMessageEs.EMAIL_ALREADY_HAS_ACCOUNT
            : ErrorMessageEn.EMAIL_ALREADY_HAS_ACCOUNT;
        } else if (error?.description) {
          message = `${notCreated}: ${error.description}`;
        }
        errorHandler('[EmailInternalAuthAction]', message, error, true);
        return {
          error: {
            status: error?.statusCode ?? 'CUSTOM_ERROR',
            error: message,
            data: error,
          } as FetchBaseQueryError,
        };
      }
      const createUserResponse = (await baseQuery({
        url: '/candidates/internal/create',
        method: 'POST',
        body: mapUserData(arg.user, emailResponse.Id ?? '', referralId),
      })) as QueryReturnValue<UserCandidate>;

      if (!createUserResponse.data) {
        errorHandler(
          '[EmailInternalAuthAction]',
          (arg.lang ?? 'en') === 'es'
            ? ErrorMessageEs.USER_NOT_CREATED
            : ErrorMessageEn.USER_NOT_CREATED,
          'Could not create user',
          true
        );
        throw new CustomFetchQueryError(
          500,
          createUserResponse.data,
          'Could not create user'
        );
      }

      tracker.identify(createUserResponse.data.user.Id, {
        name: `${createUserResponse.data.user.FirstName} ${createUserResponse.data.user.LastName}`,
        email: createUserResponse.data.user.Email,
        type: UserTypes.CANDIDATE,
        role: UserTypes.CANDIDATE,
      });

      tracker.emit('User Registered', {
        account_type: 'Candidate',
        register_type: 'email_pass',
        site_version: localStorage.getItem('appVersion') ?? arg.siteVersion,
      });

      successHandler(
        (arg.lang ?? 'en') === 'es'
          ? SuccessMessageEs.USER_CREATED
          : SuccessMessageEn.USER_CREATED,
        true
      );

      return createUserResponse;
    },
    invalidatesTags: ['Candidates'],
  });
